import React from 'react';
import { motion } from 'framer-motion';
import { FiExternalLink, FiGithub } from 'react-icons/fi';
import { useInView } from 'react-intersection-observer';
import './Projects.css';

const Projects = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const projects = [
    {
      id: 1,
      title: 'EarthSearch.ai',
      subtitle: 'AI-Powered Location Information Platform',
      description: 'An intelligent platform that provides comprehensive location information including history, landmarks, culture, and local insights powered by AI, along with weather forecasts for any location worldwide.',
      category: 'ai',
      tags: ['Python', 'JavaScript', 'CSS', 'HTML', 'Flask', 'OpenAI API', 'YouTube API', 'OpenWeatherMap API'],
      liveUrl: 'https://www.earthsearch.ai',
      githubUrl: '#',
      image: `${process.env.PUBLIC_URL}/images/earthsearch-screenshot.jpg`,
      features: [
        'AI-powered location information and insights',
        'Historical facts and cultural knowledge',
        'Landmark and points of interest discovery',
        'Weather forecasts and current conditions',
        'Interactive location search and exploration',
        'Rich multimedia content integration'
      ],
      status: 'live',
      featured: true,
      color: '#10b981'
    },
    {
      id: 2,
      title: 'USCovidMap.io',
      subtitle: 'Interactive COVID-19 Data Visualization',
      description: 'An interactive map platform providing comprehensive visualization of US COVID-19 data over time, featuring county-level statistics and temporal analysis.',
      category: 'visualization',
      tags: ['Python', 'JavaScript', 'CSS', 'HTML', 'Flask', 'Mapbox', 'COVID-19 Data (NYT)'],
      liveUrl: 'https://www.uscovidmap.io',
      githubUrl: '#',
      image: `${process.env.PUBLIC_URL}/images/uscovidmap-screenshot.jpg`,
      features: [
        'Interactive county-level COVID-19 maps',
        'Time-series visualization and playback',
        'Comparative analysis tools',
        'Real-time data updates from CDC',
        'Mobile-responsive design',
        'Data export functionality'
      ],
      status: 'live',
      featured: true,
      color: '#3b82f6'
    },
    {
      id: 3,
      title: 'Drug Shortage Analysis Dashboard',
      subtitle: 'Business Intelligence Dashboard for FDA Drug Shortage Data',
      description: 'A comprehensive business intelligence dashboard that analyzes U.S. drug shortage and enforcement data sourced from the FDA, featuring AI-powered insights and interactive visualizations for healthcare professionals and analysts.',
      category: 'ai',
      tags: ['Python', 'Flask', 'PostgreSQL', 'AWS', 'D3.js', 'OpenAI GPT', 'FDA OpenFDA API', 'HTML/CSS', 'JavaScript'],
      liveUrl: 'https://drug-shortage-analysis-mc2615.pythonanywhere.com/',
      githubUrl: '#',
      image: `${process.env.PUBLIC_URL}/images/drug-shortage-screenshot.jpg`,
      features: [
        'AI-powered drug shortage analysis and insights generation',
        'Daily FDA data integration and automated monitoring',
        'Interactive trend analysis and data visualizations',
        'Company-specific filtering and deep-dive analysis capabilities',
        'Geographic distribution mapping of drug shortages',
        'Recall severity classification and comprehensive tracking'
      ],
      status: 'live',
      featured: true,
      color: '#ef4444'
    },
    {
      id: 4,
      title: 'Personal Portfolio Website',
      subtitle: 'Modern React Portfolio with Advanced Features',
      description: 'A sophisticated personal portfolio website built with React, featuring modern animations, dark mode support, responsive design, and comprehensive skills showcase with interactive filtering.',
      category: 'web',
      tags: ['React', 'JavaScript', 'CSS3', 'HTML5', 'Framer Motion', 'React Icons', 'GitHub Pages'],
      liveUrl: '#',
      githubUrl: '#',
      image: `${process.env.PUBLIC_URL}/images/portfolio-screenshot.jpg`,
      features: [
        'Modern React 18 with functional components and hooks',
        'Framer Motion animations and smooth transitions',
        'Dark/Light mode toggle with localStorage persistence',
        'Responsive design optimized for all device sizes',
        'Interactive skills filtering and categorization',
        'Professional project showcase with detailed descriptions'
      ],
      status: 'live',
      featured: true,
      color: '#7c3aed'
    }
  ];



  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const ProjectCard = ({ project }) => (
    <motion.div
      className={`project-card ${project.featured ? 'project-card--featured' : ''} ${!project.image ? 'project-card--no-image' : ''}`}
      variants={itemVariants}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.3 }}
      style={{ '--project-color': project.color }}
    >
      {project.image ? (
        <div className="project-image">
          <img src={project.image} alt={project.title} />
          <div className="project-overlay">
            <div className="project-actions">
              <a 
                href={project.liveUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="project-action project-action--primary"
                title="Visit Site"
              >
                <FiExternalLink />
                <span>Visit Site</span>
              </a>
              {project.githubUrl !== '#' && (
                <a 
                  href={project.githubUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="project-action project-action--secondary"
                  title="View Source Code"
                >
                  <FiGithub />
                  <span>Code</span>
                </a>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="project-content">
        <div className="project-header">
          <div className="project-title-group">
            <h3 className="project-title">{project.title}</h3>
            <p className="project-subtitle">{project.subtitle}</p>
          </div>
        </div>

        <p className="project-description">{project.description}</p>

        <div className="project-features">
          <h4 className="project-features-title">Key Features</h4>
          <ul className="project-features-list">
            {project.features.slice(0, 3).map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>
        </div>

        <div className="project-technologies">
          <div className="project-tech-label">Technologies Used:</div>
          <div className="project-tech-tags">
            {project.tags.map((tech, index) => (
              <span key={index} className="project-tech-tag">
                {tech}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <section id="projects" className="section projects-section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            Featured Projects
          </motion.h2>

          {/* Projects Grid */}
          <motion.div 
            className="projects-grid" 
            variants={itemVariants}
            layout
          >
            {projects.map((project) => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </motion.div>


        </motion.div>
      </div>
    </section>
  );
};

export default Projects; 